/**
 * Act1TopBar — 第一幕顶部栏
 *
 * 显示轮次、亲友态度条、设置按钮
 */
import React from 'react';
import { Settings, Users } from 'lucide-react';
import type { FamilyMember } from '@shared/types/family';
import { ACT1_MAX_ROUNDS } from '@shared/constants/gameBalance';

interface Act1TopBarProps {
  round: number;
  familyA: FamilyMember[];
  familyB: FamilyMember[];
  familyAttitudes: Record<string, number>;
  onSettingsClick?: () => void;
}

export const Act1TopBar: React.FC<Act1TopBarProps> = React.memo(({
  round,
  familyA,
  familyB,
  familyAttitudes,
  onSettingsClick,
}) => {
  return (
    <div className="bg-white/80 backdrop-blur-md border-b border-[#E5E0D8] flex flex-col z-10 shrink-0">
      {/* Turn counter + settings */}
      <div className="flex items-center justify-center pt-1.5 pb-0.5 relative">
        <span className="text-[10px] font-medium text-[#9CA3AF]">
          第一幕 · 亲友初筛 · 第 {round}/{ACT1_MAX_ROUNDS} 轮
        </span>
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#F5EDE3] transition-colors text-[#C4B9AC] hover:text-[#6B7280]"
            aria-label="设置"
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {/* Family attitude bars */}
      <div className="px-3 pb-2 space-y-1">
        <FamilySideBar label="男方亲友" members={familyA} attitudes={familyAttitudes} color="coral" />
        <FamilySideBar label="女方亲友" members={familyB} attitudes={familyAttitudes} color="purple" />
      </div>
    </div>
  );
});

Act1TopBar.displayName = 'Act1TopBar';

// ── 单侧亲友态度条 ──────────────────────────────────

interface FamilySideBarProps {
  label: string;
  members: FamilyMember[];
  attitudes: Record<string, number>;
  color: 'coral' | 'purple';
}

const FamilySideBar: React.FC<FamilySideBarProps> = ({ label, members, attitudes, color }) => {
  const colors = color === 'coral'
    ? { bg: 'bg-coral-100', fill: 'bg-coral-500', text: 'text-coral-700', icon: 'text-coral-400' }
    : { bg: 'bg-purple-100', fill: 'bg-purple-500', text: 'text-purple-700', icon: 'text-purple-400' };

  return (
    <div className="flex items-center gap-1.5">
      <Users size={10} className={colors.icon} />
      <span className={`text-[9px] ${colors.text} font-medium w-12 shrink-0`}>{label}</span>
      <div className="flex-1 flex gap-1">
        {members.map(m => {
          const val = attitudes[m.id] ?? 0;
          // Normalize -50~50 to 0~100%
          const pct = Math.max(0, Math.min(100, (val + 50)));
          return (
            <div key={m.id} className="flex-1 flex flex-col items-center gap-0.5">
              <div className={`w-full h-1.5 ${colors.bg} rounded-full overflow-hidden`}>
                <div
                  className={`h-full rounded-full transition-all duration-500 ${pct >= 50 ? colors.fill : 'bg-red-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[8px] text-[#9CA3AF] truncate max-w-full">{m.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
